import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import ItemCustomization from './ItemCustomization';

const MenuCategory = ({ category, onAddToCart }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomization, setShowCustomization] = useState(false);

  const handleItemClick = (item) => {
    if (item.customizations && Object.keys(item.customizations).length > 0) {
      setSelectedItem(item);
      setShowCustomization(true);
    } else {
      onAddToCart(item);
    }
  };

  const handleCustomizationComplete = (item, customizations) => {
    onAddToCart(item, customizations);
    setShowCustomization(false);
    setSelectedItem(null);
  };

  if (!category) return null;

  return (
    <div className="space-y-6">
      {/* Category Header */}
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
          <Icon name={category.icon} size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="font-headline text-2xl font-bold text-text-primary">{category.name}</h2>
          <p className="text-text-secondary">{category.description}</p>
        </div>
      </div>

      {/* Menu Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {category.items.map((item) => (
          <div
            key={item.id}
            className="bg-background rounded-lg shadow-warm hover:shadow-warm-lg transition-organic overflow-hidden cursor-pointer"
            onClick={() => handleItemClick(item)}
          >
            {/* Item Image */}
            <div className="relative h-48 overflow-hidden">
              <Image
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover hover:scale-105 transition-organic"
              />
              {item.popular && (
                <div className="absolute top-3 left-3 bg-conversion text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Popular
                </div>
              )}
              {item.vegetarian && (
                <div className="absolute top-3 right-3 bg-trust text-white p-2 rounded-full">
                  <Icon name="Leaf" size={16} />
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full">
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-primary">{item.prepTime}</span>
                </div>
              </div>
            </div>

            {/* Item Details */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-text-primary mb-1">{item.name}</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="font-bold text-xl text-primary">${item.price}</div>
                </div>
              </div>

              {/* Spice Level Indicator */}
              {item.spiceLevel && (
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-text-secondary">Spice Level:</span>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`w-2 h-2 rounded-full ${
                          level <= item.spiceLevel ? 'bg-warning' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Cart Button */}
              <button className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition-organic flex items-center justify-center space-x-2">
                <Icon name="Plus" size={18} />
                <span>Add to Cart</span>
              </button>

              {/* Customization Hint */}
              {item.customizations && Object.keys(item.customizations).length > 0 && (
                <p className="text-center text-text-secondary text-sm mt-2">
                  Click to customize your order
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Customization Modal */}
      {showCustomization && selectedItem && (
        <ItemCustomization
          item={selectedItem}
          onComplete={handleCustomizationComplete}
          onClose={() => {
            setShowCustomization(false);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
};

export default MenuCategory;